import {Timer} from '@gongxigames/framework/core';
import {GenericPopupData} from '@gongxigames/framework/slot/types';
import {GenericPopupEvents} from '@gongxigames/framework/slot/modules/genericPopup/GenericPopupEvents';
import {
    GenericPopupModule, GenericPopupModuleEvents
} from '@gongxigames/framework/slot/modules/genericPopup/GenericPopupModule';
import {GenericPopupView} from '@gongxigames/framework/slot/modules/genericPopup/GenericPopupView';
import {strictGameEmitter} from '@gongxigames/framework/slotV2/globals';
import {ModuleState} from '@gongxigames/framework/slot';

export class GenericPopupShowingState extends ModuleState<GenericPopupView, GenericPopupModule, GenericPopupModuleEvents> {
    protected _closeTimer?: Timer;

    public onEnterState(): void {
        this.emitStartEvent();
        this.tryToShowPopup();
    }

    public onLeaveState(): void {
    }

    protected setupEvents(): void {
        this.setEventHandler('GenericPopupModuleEvents.CONTINUE_BUTTON_CLICKED', this.onContinueButtonClicked.bind(this));
        this.setEventHandler('GenericPopupModuleEvents.CLOSED', this.onClosed.bind(this));
        this.setEventHandler('GenericPopupModuleEvents.FADE_OUT_STARTED', this.onFadeOutStarted.bind(this));
        this.setEventHandler(GenericPopupEvents.SHOW_POPUP, this.onShowPopup.bind(this));
        this.setEventHandler('General.Popup.SHOW', this.showPopup.bind(this));
        this.setEventHandler('General.UI.SPACEBAR_UP', this.spacebarClicked.bind(this));
    }

    protected tryToShowPopup(): void {
        const {startTimeout} = this.view.config;

        this.view.playWaitingSound();
        this.view.playTransitionEnterSound();

        if (startTimeout) {
            this.module.updateManager.addTimer(this.playFaderTween.bind(this), startTimeout, true);
        } else {
            this.playFaderTween();
        }
    }

    protected playFaderTween(): void {
        const {closeTimeout} = this.view.config;

        if (closeTimeout) {
            this._closeTimer = new Timer(this.onContinueButtonClicked.bind(this), closeTimeout, true);
            this.module.updateManager.add(this._closeTimer);
        }

        this.emitOutroStartedEvent(this.view.getPopupEmitType());
        this.view.playFaderTween();
    }

    protected onShowPopup(type: symbol, ...args: any[]): void {
        this.module.queue.push({type, args});
    }

    protected showPopup(data: GenericPopupData): void {
        this.module.queue.push({type: data.type, args: data});
    }

    protected spacebarClicked(): void {
        if (this.view.continueButton && this.view.continueButton.enabled) {
            this.view.onContinueButtonClicked();
        }
    }

    protected clearCloseTimer(): void {
        if (this._closeTimer) {
            this._closeTimer.stop();
            this._closeTimer = undefined;
        }
    }

    protected onContinueButtonClicked(): void {
        this.view.disableContinueButton();
        this.clearCloseTimer();
        this.view.playFadeOutAnimation();

        this.emit(GenericPopupEvents.POPUP_BUTTON_CLICKED, this.view.getPopupEmitType());
    }

    protected onClosed(type: symbol): void {
        this.emitOutroEvent(type);
        this.view.playExitSound();

        this.module.gotoState(GenericPopupModule.BASIC_STATE);

        const nextPopup = this.module.queue.shift();

        if (nextPopup) {
            if (Array.isArray(nextPopup.args)) {
                this.emit(GenericPopupEvents.SHOW_POPUP, nextPopup.type, ...nextPopup.args);
            } else {
                this.emit('General.Popup.SHOW', nextPopup.args);
            }
        }
    }

    protected emitOutroStartedEvent(type: symbol): void {
        this.emit(GenericPopupEvents.INTRO_ANIMATION_STARTED, type);
    }

    protected emitStartEvent(): void {
        this.emit(GenericPopupEvents.INTRO_STARTED, this.view.getPopupEmitType());
        strictGameEmitter.emit('Generic.POPUP.INTRO_STARTED', this.view.getPopupEmitType());
    }

    protected emitOutroEvent(type: symbol): void {
        this.emit(GenericPopupEvents.POPUP_CLOSED, type);
        strictGameEmitter.emit('Generic.POPUP.POPUP_CLOSED', this.view.getPopupEmitType());
    }

    protected onFadeOutStarted(type: symbol): void {
        this.emit(GenericPopupEvents.FADE_OUT_STARTED, type);
        strictGameEmitter.emit('Generic.POPUP.FADE_OUT_STARTED', this.view.getPopupEmitType());
    }
}
